// eslint-disable-next-line no-unused-vars
import React from 'react';
import { bool, func, object, string } from 'prop-types';
import { withTranslation } from 'react-i18next';
import { dequal } from 'dequal';
import omit from 'lodash/omit';

import { CartItemPropType } from 'Types/cart';
import ProductCartItemComponent from 'Components/Products/ProductCartItem';
import ProductAbstract from 'Containers/ProductAbstract';
import withCart from 'AppSrc/hocs/withCart';
import withToastNotifications from 'AppSrc/hocs/withToastNotifications';

export class ProductCartItem extends ProductAbstract {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      loading: false,
      isRefreshing: false,
      addToCartLoading: false,
      error: null,
      product: {
        ...this.state.product,
        productId: props.product.productId,
        id: props.product.productId
      }
    };
    if (
      props.product?.pictures?.length
      && (
        !this.state.product.pictureUrl
        || this.state.product.pictureUrl.includes('no-image-available')
      )
    ) {
      this.state.product.pictureUrl = props.product?.pictures?.at(0)?.urlOverlayPicture;
    }
    this.handleOnDelete = this.handleOnDelete.bind(this);
    this.handleOnChangeQuantity = this.handleOnChangeQuantity.bind(this);
    this.handleOnQuantityRefresh = this.handleOnQuantityRefresh.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    const { product: stateProduct } = state;
    const { product: propsProduct } = props;

    const updatedPromoItems = Object.entries(propsProduct?.promoItems || {}).reduce((acc, [key, products]) => {
      const statePromoItems = (stateProduct?.promoItems || {})[key];
      acc[key] = (Array.isArray(products) ? products : [products]).map(prod => {
        const statePromoProd = (Array.isArray(statePromoItems) ? statePromoItems : [statePromoItems])?.find(
          p => p?.itemId && prod?.itemId && p.itemId === prod.itemId
        ) || {};

        return {
          ...statePromoProd,
          ...prod,
        };
      });
      return acc;
    }, {});

    return {
      ...state,
      product: {
        ...omit(stateProduct, ['updated', 'new', 'deleted', 'originalQuantity']),
        ...(propsProduct || {}),
        promoItems: updatedPromoItems,
      },
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.isRefreshing !== nextState.isRefreshing
      || this.props.defaultCurrency !== nextProps.defaultCurrency
      || this.props.isExternalReseller !== nextProps.isExternalReseller
      || !dequal(this.state.product, nextState.product)
      || super.shouldComponentUpdate(nextProps, nextState)
    );
  }

  /**
   * Delete product from cart
   */
  handleOnDelete() {
    const { cart } = this.props;
    const { product } = this.state;
    this.setState({ loading: true, error: null }, async () => {
      try {
        await cart.removeProduct(product);
      } catch (e) {
        this.setState({ error: e.message });
      } finally {
        this.setState({ loading: false });
      }
    });
  }

  /**
   * Adjusts quantity
   */
  handleOnQuantityRefresh() {
    const { cart } = this.props;
    const { product } = this.state;
    this.setState({ isRefreshing: true }, async () => {
      try {
        if (product.minQuantity > product.stockValue) {
          await cart.removeProduct(product);
        } else {
          await cart.updateProduct({
            ...product,
            quantity: Math.min(product.quantity, product.stockValue)
          });
        }
      } catch (e) {
        this.setState({ error: e.message });
      } finally {
        this.setState({ isRefreshing: false });
      }
    });
  }

  /**
   * Update product qty
   * @param quantity
   */
  handleOnChangeQuantity(quantity) {
    const { product } = this.state;
    const { toast, t, cart } = this.props;
    if (
      quantity > product.stockValue
      || product.stockValue < product.minQuantity
    ) return;
    super.handleOnChangeQuantity(quantity, prod => {
      if (product.quantity !== prod.quantity) {
        this.setState({ loading: true, error: null }, async () => {
          try {
            if (cart.hasProduct(prod)) {
              this.setState({ addToCartLoading: true });
              await cart.updateProduct(prod);
              this.setState({ addToCartLoading: false });
            }
          } catch (e) {
            toast.addNotification({
              title: t('cart.failedQtyUpdate'),
              type: 'error'
            });
            super.handleOnChangeQuantity(product.quantity);
          } finally {
            this.setState({ loading: false });
          }
        });
      }
    });
  }

  render() {
    const { product, error, loading, isRefreshing } = this.state;
    const { defaultCurrency, isExternalReseller } = this.props;
    return (
      <ProductCartItemComponent
        error={ error?.message || error }
        loading={ loading }
        product={ product }
        isRefreshing={ isRefreshing }
        onQuantityRefresh={ this.handleOnQuantityRefresh }
        onChange={ this.handleOnChangeQuantity }
        onDelete={ this.handleOnDelete }
        defaultCurrency={ defaultCurrency }
        isExternalReseller={ isExternalReseller }
      />
    );
  }
}

ProductCartItem.propTypes = {
  product: CartItemPropType,
  cart: object,
  toast: object,
  t: func,
  defaultCurrency: string,
  isExternalReseller: bool
};
ProductCartItem.displayName = 'ProductCartItem';

export default withTranslation()(withToastNotifications(withCart(ProductCartItem)));
